import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ReportFormat } from '@/application/metrics';
import { buildFinanceHeroSnapshot, buildCajaMovimientosHref } from '@/application/metrics';
import { persistReportScope } from '@/application/metrics/metricsScope';
import { canExportReports } from '@/application/permissions/canExportReports';
import {
  downloadCajaReport,
  downloadComparisonReport,
  downloadDirectoryPack,
  getCajaReporte,
  getOwnerDashboardPair,
  getSedes,
} from '@/infrastructure/http/metricsApi';
import { metricsKeys } from '@/infrastructure/query/metricsKeys';
import { DomainPageHeader } from '@/presentation/components/domain/DomainPageHeader';
import { DomainScopeBar } from '@/presentation/components/domain/DomainScopeBar';
import { DomainTabs } from '@/presentation/components/domain/DomainTabs';
import { FinanceHeroStrip } from '@/presentation/components/domain/FinanceHeroStrip';
import { ExportButtons } from '@/presentation/components/metrics/ExportButtons';
import {
  CajaReporteBlock,
  MetricKpiCard,
  MetricPrimaryStrip,
  PlanRankingBlock,
  TrendBlock,
} from '@/presentation/components/metrics';
import { formatCurrency } from '@/presentation/components/metrics/metricFormatters';
import { PermissionGate } from '@/presentation/components/permissions/PermissionGate';
import { previousMonthOf, useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';
import { formatMoneyCompact, formatSigned, relativeChangePct } from '@/presentation/utils/metricDeltas';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { PanelPageState } from '@/presentation/components/common/PanelPageState';
import { MetricsState } from '@/presentation/components/metrics/MetricsState';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { firstVisibleHousePath } from '@/application/navigation/menuHouses';

type FinanceTab = 'network' | 'overview' | 'trend' | 'plans' | 'methods' | 'movements';

const FINANCE_TABS: FinanceTab[] = ['network', 'overview', 'trend', 'plans', 'methods', 'movements'];

function parseFinanceTab(
  raw: string | null,
  multiClinic: boolean,
  hasTenant: boolean
): FinanceTab {
  if (raw && (FINANCE_TABS as string[]).includes(raw)) {
    const tab = raw as FinanceTab;
    if (tab === 'network' && !multiClinic) return 'overview';
    return tab;
  }
  // Deep-link con clínica (CTA de ingresos): overview, no flash de “red”.
  if (multiClinic && hasTenant) return 'overview';
  return multiClinic ? 'network' : 'overview';
}

/** Dominio finanzas: red → clínica → evolución → planes → medios → movimientos. */
export function FinancesPage() {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const { hasPermission } = usePermissions();
  const clinicNames = useClinicNames();
  const [searchParams, setSearchParams] = useSearchParams();
  const { period, year, month, periodQuery, label: periodLabel } = useGlobalPeriod();
  const locale = i18n.language;

  const tenantIds = useMemo(
    () => user?.scope.allowedTenantIds ?? [],
    [user?.scope.allowedTenantIds]
  );
  const multiClinic = tenantIds.length > 1;
  const clinicOptions = useMemo(
    () => tenantIds.map((id) => ({ value: id, label: clinicNames[id] ?? id })),
    [tenantIds, clinicNames]
  );

  const tenantFromQuery = searchParams.get('tenant') ?? '';
  const sedeFromQuery = searchParams.get('sede') ?? searchParams.get('consultorioId') ?? '';
  const tabFromQuery = searchParams.get('tab');
  const [clinicId, setClinicId] = useState(
    () => (tenantFromQuery && tenantIds.includes(tenantFromQuery) ? tenantFromQuery : tenantIds[0] ?? '')
  );
  const [sedeId, setSedeId] = useState<string>(() => (sedeFromQuery ? sedeFromQuery : 'all'));
  const [tab, setTab] = useState<FinanceTab>(() =>
    parseFinanceTab(tabFromQuery, multiClinic, Boolean(tenantFromQuery && tenantIds.includes(tenantFromQuery)))
  );

  const canView =
    hasPermission('metrics.caja') ||
    hasPermission('metrics.tendencia') ||
    hasPermission('metrics.ranking_planes');
  const canExport = canExportReports(hasPermission);
  const canExportDirectoryPack = canExport && tenantIds.length > 0;
  const canViewCaja = hasPermission('metrics.caja') || canExport;
  const needsCaja = tab === 'methods';

  const consultorioId = useMemo(() => {
    if (sedeId === 'all' || sedeId === '') return undefined;
    const n = Number(sedeId);
    return Number.isFinite(n) ? n : undefined;
  }, [sedeId]);

  useEffect(() => {
    if (tenantFromQuery && tenantIds.includes(tenantFromQuery) && tenantFromQuery !== clinicId) {
      setClinicId(tenantFromQuery);
    }
  }, [tenantFromQuery, tenantIds, clinicId]);

  useEffect(() => {
    const next = parseFinanceTab(
      tabFromQuery,
      multiClinic,
      Boolean(tenantFromQuery && tenantIds.includes(tenantFromQuery))
    );
    setTab((prev) => (prev === next ? prev : next));
  }, [tabFromQuery, multiClinic, tenantFromQuery, tenantIds]);

  useEffect(() => {
    if (sedeFromQuery && sedeFromQuery !== sedeId) {
      setSedeId(sedeFromQuery);
    }
  }, [sedeFromQuery, sedeId]);

  useEffect(() => {
    if (!clinicId && clinicOptions[0]?.value) {
      setClinicId(clinicOptions[0].value);
    }
  }, [clinicId, clinicOptions]);

  useEffect(() => {
    if (clinicId && tab !== 'network') {
      persistReportScope(period, clinicId);
    }
  }, [clinicId, period, tab]);

  const sedesQuery = useQuery({
    queryKey: metricsKeys.sedes(clinicId),
    queryFn: () => getSedes(token!, clinicId),
    enabled: Boolean(token && clinicId && tab !== 'network' && canView),
  });

  const sedes = useMemo(() => sedesQuery.data ?? [], [sedesQuery.data]);
  const sedeOptions = useMemo(
    () => sedes.map((s) => ({ value: String(s.id), label: s.nombre })),
    [sedes]
  );

  useEffect(() => {
    if (sedeId === 'all' || sedes.length === 0) return;
    const ok = sedes.some((s) => String(s.id) === sedeId);
    if (!ok) setSedeId('all');
  }, [sedes, sedeId]);

  const previousPeriod = useMemo(() => previousMonthOf(year, month), [year, month]);

  const cajaQuery = useQuery({
    queryKey: metricsKeys.cajaReporte(clinicId, year, month, consultorioId),
    queryFn: () =>
      getCajaReporte(token!, {
        tenantId: clinicId,
        year,
        month,
        consultorioId,
      }),
    enabled: Boolean(token && clinicId && canView && canViewCaja),
  });

  const cajaPrevQuery = useQuery({
    queryKey: metricsKeys.cajaReporte(
      clinicId,
      previousPeriod.year,
      previousPeriod.month,
      consultorioId
    ),
    queryFn: () =>
      getCajaReporte(token!, {
        tenantId: clinicId,
        year: previousPeriod.year,
        month: previousPeriod.month,
        consultorioId,
      }),
    enabled: Boolean(token && clinicId && canView && canViewCaja),
  });

  const pairRange =
    periodQuery.fromMonth !== undefined && periodQuery.toMonth !== undefined
      ? { fromMonth: periodQuery.fromMonth, toMonth: periodQuery.toMonth }
      : undefined;
  const networkQuery = useQuery({
    queryKey: metricsKeys.ownerDashboardPair(year, month, pairRange),
    queryFn: () => getOwnerDashboardPair(token!, periodQuery),
    enabled: Boolean(token && multiClinic && canView && canViewCaja),
  });

  const patchSearchParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === '' || value === 'all') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleClinicChange = (id: string) => {
    setClinicId(id);
    setSedeId('all');
    patchSearchParams({ tenant: id, sede: null, consultorioId: null });
  };

  const handleSedeChange = (id: string) => {
    setSedeId(id);
    patchSearchParams({
      sede: id === 'all' ? null : id,
      consultorioId: null,
      tenant: clinicId || null,
    });
  };

  const handleTabChange = (id: string) => {
    const next = id as FinanceTab;
    setTab(next);
    patchSearchParams({
      tab: next,
      tenant: next === 'network' ? null : clinicId || null,
    });
  };

  const handleExport = useCallback(
    (format: ReportFormat) =>
      downloadCajaReport(
        token ?? '',
        { tenantId: clinicId, year, month, consultorioId },
        format
      ),
    [token, clinicId, year, month, consultorioId]
  );

  const handleNetworkExport = useCallback(
    (format: ReportFormat) => downloadComparisonReport(token ?? '', { year, month }, format),
    [token, year, month]
  );

  const handleDirectoryPack = useCallback(
    (format: ReportFormat) =>
      downloadDirectoryPack(token ?? '', { year, month }, format, {
        filename: t('dashboard.directoryPack.filename', { period }),
      }),
    [token, year, month, period, t]
  );

  const network = networkQuery.data;
  const networkRows = useMemo(() => {
    if (!network) return [];
    const prevMap = new Map(network.previous.clinics.map((c) => [c.tenantId, c.summary]));
    return network.current.clinics
      .map((row) => {
        const prev = prevMap.get(row.tenantId);
        const ingresos = row.summary.caja.ingresos;
        const egresos = row.summary.caja.egresos;
        const neto = row.summary.caja.neto;
        const prevIngresos = prev?.caja.ingresos ?? 0;
        return {
          tenantId: row.tenantId,
          name: clinicNames[row.tenantId] ?? row.tenantId,
          ingresos,
          egresos,
          neto,
          ingresosDelta: relativeChangePct(ingresos, prevIngresos),
        };
      })
      .sort((a, b) => b.ingresos - a.ingresos);
  }, [network, clinicNames]);

  const networkTotals = network?.current.totals.caja;
  const caja = cajaQuery.data ?? null;
  const isLoading =
    tab === 'network'
      ? networkQuery.isLoading
      : needsCaja
        ? cajaQuery.isLoading
        : false;
  const error =
    tab === 'network'
      ? networkQuery.error instanceof Error
        ? networkQuery.error.message
        : null
      : needsCaja && cajaQuery.error instanceof Error
        ? cajaQuery.error.message
        : null;

  const heroSnapshot = useMemo(() => {
    if (!canViewCaja) return null;

    if (multiClinic && tab === 'network') {
      const current = network?.current.totals.caja;
      if (!current) return null;
      const previous = network.previous.totals.caja;
      return buildFinanceHeroSnapshot({
        scope: 'network',
        current: {
          neto: current.neto,
          ingresos: current.ingresos,
          egresos: current.egresos,
          movimientos: current.movimientos,
        },
        previous: previous
          ? {
              neto: previous.neto,
              ingresos: previous.ingresos,
              movimientos: previous.movimientos,
            }
          : null,
      });
    }

    if (!caja) return null;
    const prev = cajaPrevQuery.data;
    return buildFinanceHeroSnapshot({
      scope: 'clinic',
      current: {
        neto: caja.neto,
        ingresos: caja.ingresos,
        egresos: caja.egresos,
        movimientos: caja.movimientos,
        porFormaPago: caja.porFormaPago,
      },
      previous: prev
        ? {
            neto: prev.neto,
            ingresos: prev.ingresos,
            movimientos: prev.movimientos,
            porFormaPago: prev.porFormaPago,
          }
        : null,
    });
  }, [canViewCaja, multiClinic, tab, network, caja, cajaPrevQuery.data]);

  const heroLoading =
    canViewCaja &&
    !heroSnapshot &&
    (multiClinic && tab === 'network'
      ? networkQuery.isLoading || networkQuery.isFetching
      : cajaQuery.isLoading || cajaQuery.isFetching);
  const heroError =
    canViewCaja && !heroSnapshot
      ? multiClinic && tab === 'network'
        ? networkQuery.error instanceof Error
          ? networkQuery.error.message
          : null
        : cajaQuery.error instanceof Error
          ? cajaQuery.error.message
          : null
      : null;

  const tabs = useMemo(() => {
    const items: Array<{ id: FinanceTab; label: string }> = [];
    if (multiClinic) {
      items.push({ id: 'network', label: t('domain.finances.tabs.network') });
    }
    items.push(
      { id: 'overview', label: t('domain.finances.tabs.overview') },
      { id: 'trend', label: t('domain.finances.tabs.trend') },
      { id: 'plans', label: t('domain.finances.tabs.plans') },
      { id: 'methods', label: t('domain.finances.tabs.methods') },
      { id: 'movements', label: t('domain.finances.tabs.movements') }
    );
    return items;
  }, [multiClinic, t]);

  const movimientosHref = useMemo(
    () =>
      buildCajaMovimientosHref({
        tenantId: clinicId,
        year,
        month,
        period,
        sede: consultorioId,
        from: 'finances',
      }),
    [clinicId, period, year, month, consultorioId]
  );

  const showSede = tab !== 'network' && sedeOptions.length > 0;

  if (!canView) {
    return <AccessDenied domain="finances" homeTo={firstVisibleHousePath(hasPermission)} />;
  }

  return (
    <div className="panel-page panel-page--fixed panel-safe-bottom bi-surface flex h-full min-h-0 flex-col gap-4 !space-y-0">
      <div className="shrink-0 space-y-4">
        <DomainPageHeader
          question={t('domain.finances.question')}
          title={t('domain.finances.title')}
          subtitle={t('domain.finances.subtitle', {
            period: periodLabel,
          })}
        />

        <DomainScopeBar
          showClinic={tab !== 'network'}
          clinicId={clinicId}
          clinicOptions={clinicOptions}
          onClinicChange={handleClinicChange}
          showSede={showSede}
          sedeId={sedeId}
          sedeOptions={sedeOptions}
          onSedeChange={handleSedeChange}
          actions={
            canExportDirectoryPack || canExport ? (
              <div className="flex flex-wrap items-center justify-end gap-3">
                {canExportDirectoryPack ? (
                  <div data-testid="directory-pack-export">
                    <ExportButtons
                      label={t('dashboard.directoryPack.label')}
                      onExport={handleDirectoryPack}
                      compact
                      touchFriendly
                    />
                  </div>
                ) : null}
                {canExport ? (
                  <div data-testid={tab === 'network' ? 'comparison-export' : 'caja-export'}>
                    <ExportButtons
                      onExport={tab === 'network' ? handleNetworkExport : handleExport}
                      compact
                    />
                  </div>
                ) : null}
              </div>
            ) : undefined
          }
        />

        <FinanceHeroStrip
          snapshot={heroSnapshot}
          locale={locale}
          loading={heroLoading}
          error={heroError}
          forbidden={canView && !canViewCaja}
        />

        <DomainTabs
          tabs={tabs}
          active={tab}
          onChange={handleTabChange}
          ariaLabel={t('domain.finances.tabsLabel')}
        />

        {error && (
          <PanelPageState variant="error" layout="inline" onRetry={() => window.location.reload()} />
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2">
        {tab === 'network' && multiClinic && (
          <div className="space-y-4">
            {isLoading ? (
              <div className="panel-soft-card">
                <LoadingBlock minHeightClassName="min-h-[10rem]" />
              </div>
            ) : (
              <>
                {networkTotals && (
                  <MetricPrimaryStrip aria-label={t('domain.finances.kpis.networkStrip')}>
                    <MetricKpiCard
                      variant="primary"
                      label={t('domain.finances.kpis.income')}
                      value={formatCurrency(networkTotals.ingresos, locale)}
                      tone="green"
                      metricId="revenue"
                    />
                    <MetricKpiCard
                      variant="primary"
                      label={t('domain.finances.kpis.expense')}
                      value={formatCurrency(networkTotals.egresos, locale)}
                      tone="amber"
                      metricId="expense"
                    />
                    <MetricKpiCard
                      variant="primary"
                      label={t('domain.finances.kpis.net')}
                      value={formatCurrency(networkTotals.neto, locale)}
                      tone="blue"
                      metricId="net"
                    />
                  </MetricPrimaryStrip>
                )}
                {networkRows.length === 0 ? (
                  <MetricsState
                    variant="empty"
                    title={t('domain.finances.networkEmpty')}
                    message={t('metrics.states.empty.message')}
                  />
                ) : (
                  <div className="metric-secondary-panel">
                    <table className="min-w-full text-left">
                      <thead className="sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 font-semibold">{t('dashboard.command.table.clinic')}</th>
                          <th className="px-3 py-2 font-semibold">{t('domain.finances.kpis.income')}</th>
                          <th className="px-3 py-2 font-semibold">{t('domain.finances.kpis.expense')}</th>
                          <th className="px-3 py-2 font-semibold">{t('domain.finances.kpis.net')}</th>
                          <th className="px-3 py-2 font-semibold">{t('domain.finances.vsPrevious')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {networkRows.map((row) => (
                          <tr
                            key={row.tenantId}
                            className="panel-row-hover cursor-pointer border-b border-[var(--color-border)] last:border-0"
                            onClick={() => {
                              setClinicId(row.tenantId);
                              setSedeId('all');
                              setTab('overview');
                              patchSearchParams({
                                tenant: row.tenantId,
                                sede: null,
                                consultorioId: null,
                                tab: 'overview',
                              });
                            }}
                          >
                            <td className="panel-heading px-3 py-2.5 font-semibold">{row.name}</td>
                            <td className="panel-body px-3 py-2.5 tabular-nums">
                              {formatMoneyCompact(locale, row.ingresos)}
                            </td>
                            <td className="panel-body px-3 py-2.5 tabular-nums">
                              {formatMoneyCompact(locale, row.egresos)}
                            </td>
                            <td className="panel-body px-3 py-2.5 tabular-nums">
                              {formatMoneyCompact(locale, row.neto)}
                            </td>
                            <td className="panel-body px-3 py-2.5 tabular-nums">
                              {row.ingresosDelta === null
                                ? '—'
                                : `${formatSigned(locale, row.ingresosDelta)}%`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="panel-muted text-xs">{t('domain.finances.networkHint')}</p>
              </>
            )}
          </div>
        )}

        {tab !== 'network' && (
          <div className="space-y-4">
            {tab === 'overview' && clinicId ? (
              <div data-testid="finances-story" className="space-y-6">
                {/* Storyboard (#17): plata (strip) → tendencia → planes → medios → movimientos. */}
                <PermissionGate permission="metrics.tendencia">
                  <section
                    aria-labelledby="finances-story-trend"
                    className="space-y-2"
                    data-testid="finances-story-trend"
                  >
                    <div>
                      <h2 id="finances-story-trend" className="panel-story-title">
                        {t('domain.finances.story.trendTitle')}
                      </h2>
                      <p className="panel-story-hint">
                        {t('domain.finances.story.trendHint')}
                      </p>
                    </div>
                    <TrendBlock
                      tenantId={clinicId}
                      year={year}
                      month={month}
                      currency="ARS"
                      consultorioId={consultorioId}
                    />
                  </section>
                </PermissionGate>

                <PermissionGate permission="metrics.ranking_planes">
                  <section
                    aria-labelledby="finances-story-plans"
                    className="space-y-2"
                    data-testid="finances-story-plans"
                  >
                    <div>
                      <h2 id="finances-story-plans" className="panel-story-title">
                        {t('domain.finances.story.plansTitle')}
                      </h2>
                      <p className="panel-story-hint">
                        {t('domain.finances.story.plansHint')}
                      </p>
                    </div>
                    {consultorioId !== undefined ? (
                      <p className="panel-note-warning">
                        {t('domain.finances.plansBranchScopeNote')}
                      </p>
                    ) : null}
                    <PlanRankingBlock
                      tenantId={clinicId}
                      year={year}
                      month={month}
                      currency="ARS"
                    />
                  </section>
                </PermissionGate>

                {canViewCaja ? (
                  <section
                    aria-labelledby="finances-story-methods"
                    className="space-y-2"
                    data-testid="finances-story-methods"
                  >
                    <div>
                      <h2 id="finances-story-methods" className="panel-story-title">
                        {t('domain.finances.story.methodsTitle')}
                      </h2>
                      <p className="panel-story-hint">
                        {t('domain.finances.story.methodsHint')}
                      </p>
                    </div>
                    {cajaQuery.isLoading ? (
                      <div className="panel-soft-card">
                        <LoadingBlock minHeightClassName="min-h-[10rem]" />
                      </div>
                    ) : (
                      caja && (
                        <CajaReporteBlock
                          data={caja}
                          currency="ARS"
                          onExport={canExport ? handleExport : undefined}
                        />
                      )
                    )}
                  </section>
                ) : null}

                {canViewCaja ? (
                  <section
                    aria-labelledby="finances-story-movements"
                    className="panel-soft-card space-y-3 p-4"
                    data-testid="finances-story-movements"
                  >
                    <div>
                      <h2 id="finances-story-movements" className="panel-story-title">
                        {t('domain.finances.story.movementsTitle')}
                      </h2>
                      <p className="panel-story-hint">
                        {consultorioId !== undefined
                          ? t('domain.finances.movementsHintBranch')
                          : t('domain.finances.story.movementsHint')}
                      </p>
                    </div>
                    <Link
                      to={movimientosHref}
                      className="panel-btn-secondary inline-flex text-sm"
                    >
                      {t('domain.finances.openMovements')} →
                    </Link>
                  </section>
                ) : null}
              </div>
            ) : null}

            {tab === 'methods' &&
              (isLoading ? (
                <div className="panel-soft-card">
                  <LoadingBlock minHeightClassName="min-h-[10rem]" />
                </div>
              ) : (
                caja && (
                  <CajaReporteBlock
                    data={caja}
                    currency="ARS"
                    onExport={canExport ? handleExport : undefined}
                  />
                )
              ))}

            {tab === 'trend' && clinicId && (
              <TrendBlock
                tenantId={clinicId}
                year={year}
                month={month}
                currency="ARS"
                consultorioId={consultorioId}
              />
            )}

            {tab === 'plans' && clinicId && (
              <div className="space-y-2">
                {consultorioId !== undefined ? (
                  <p className="panel-note-warning">
                    {t('domain.finances.plansBranchScopeNote')}
                  </p>
                ) : null}
                <PlanRankingBlock tenantId={clinicId} year={year} month={month} currency="ARS" />
              </div>
            )}

            {tab === 'movements' && (
              <div className="panel-soft-card space-y-3 p-4">
                <p className="panel-body text-sm">
                  {consultorioId !== undefined
                    ? t('domain.finances.movementsHintBranch')
                    : t('domain.finances.movementsHint')}
                </p>
                <Link
                  to={movimientosHref}
                  className="panel-btn-secondary inline-flex text-sm"
                >
                  {t('domain.finances.openMovements')} →
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
